import * as React from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import { ROLES_KEY, fetchRoles } from './roleQueries'

/**
 * Wstrzykuje `roles` i `getRoles` z react-query do kontenera zakładki.
 *
 * Slice `roles` czytało pięć kontenerów, a szósty wołał samo pobranie. Każdy z nich
 * potrzebuje dokładnie tej samej pary propsów, więc zamiast sześciu identycznych
 * opakowań jest jedno miejsce.
 *
 * Przepuszcza ref: panel administracyjny sięga po instancję zakładki, żeby wywołać
 * na niej `removeCheckedItems()`.
 */
export function withRoles<P extends object>(Wrapped: React.ComponentType<P>) {
  const WithRoles = React.forwardRef<unknown, Omit<P, 'roles' | 'getRoles'>>(
    (props, ref) => {
      const queryClient = useQueryClient()
      const { data: roles = [] } = useQuery({
        queryKey: ROLES_KEY,
        queryFn: fetchRoles,
      })

      // Komponenty czekają na wynik `getRoles()` (np. postLogin buduje z niego
      // uprawnienia), więc musi zwracać listę, a nie samo odświeżenie.
      const getRoles = React.useCallback(
        () =>
          queryClient.fetchQuery({ queryKey: ROLES_KEY, queryFn: fetchRoles }),
        [queryClient],
      )

      return (
        <Wrapped
          ref={ref as any}
          {...(props as unknown as P)}
          roles={roles}
          getRoles={getRoles}
        />
      )
    },
  )

  WithRoles.displayName = `withRoles(${
    (Wrapped as any).displayName || (Wrapped as any).name || 'Component'
  })`

  return WithRoles
}
